// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useMemo } from 'react';

import {
  AppShell as AppShellUi,
  ConnectivityBanner,
  isRegisteredCallGroup,
  NotesTabIcon,
  NotificationBannerHost,
  TasksTabIcon,
  TopBar,
  useAccountEventNotifications,
  useUiPreferencesStore,
  VoicePlaybackProvider,
} from '@ping/ui';

import {
  createWebNotificationService,
  initWebNotifications,
} from '@app/notifications/webNotifications';
import { useCallStore } from '@calls/store/callStore';
import { useIncomingConversations } from '@features/chat/useIncomingConversations';
import { useIncomingMessages } from '@features/chat/useIncomingMessages';
import { createWebVoicePlaybackDriver } from '@features/chat/webVoicePlaybackDriver';
import { RecoveryGuideProvider } from '@features/onboarding/RecoveryGuideTour';
import { HomeTourHost, PaneGuideHost, SettingsTourHost } from '@features/onboarding/tour';
import { useSelfProfileBootstrap } from '@features/profile/useSelfProfileBootstrap';
import { useMeetingReminderScheduler } from '@features/reminders/useMeetingReminderScheduler';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { CallHost } from './hosts/CallHost';
import { pushChatRouteId } from './hosts/chatRouteUrl';
import { GlobalSearchModalHost } from './hosts/GlobalSearchModalHost';
import { SetStatusModalHost } from './hosts/SetStatusModalHost';
import { SettingsModalHost } from './hosts/SettingsModalHost';
import { MainContent } from './MainContent';
import { RouterProvider, useRouter, type WebRoute } from './RouterContext';
import { Sidebar } from './Sidebar';

export interface AppShellProps {
  /** Defaults to the current pathname / `/chat`. Useful in tests. */
  readonly initialRoute?: WebRoute;
}

/**
 * Top-level shell — left icon rail + main pane + overlay layer.
 * Matches the Figma "Navigation bar" (80px) + content layout from
 * file Y95LG1oFzU2PHIvFuMYTfJ. The visual shell now lives in
 * `packages/ui/src/shell/` so the desktop slice (apps/desktop)
 * renders the same component tree through react-native-macos.
 */
export default function AppShell(props: AppShellProps): JSX.Element {
  // Singleton voice-playback driver — outlives any individual chat
  // surface so the AudioContext + element cache aren't reallocated on
  // route changes. Wraps the whole shell so every chat bubble in
  // the tree can resolve the driver through context.
  const voiceDriver = useMemo(() => createWebVoicePlaybackDriver(), []);
  return (
    <RouterProvider {...(props.initialRoute !== undefined ? { initial: props.initialRoute } : {})}>
      <VoicePlaybackProvider driver={voiceDriver}>
        <ShellChrome />
      </VoicePlaybackProvider>
    </RouterProvider>
  );
}

function ShellChrome(): JSX.Element {
  const router = useRouter();
  // Populate the self-profile store (name + avatar) so the sidebar chip and
  // chat self-avatar render the real avatar on first paint + stay in sync.
  useSelfProfileBootstrap();

  // Open a conversation from a notification banner / OS-notification click:
  // switch to the chat surface, push the chat sub-route, and nudge
  // `useChatRouteId`'s popstate listener so the selection updates.
  const openConversationFromNotification = useCallback(
    (conversationId: string) => {
      router.push('/chat');
      pushChatRouteId(conversationId);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    },
    [router],
  );

  // Notification service (in-app banner + chime + OS notification + badge).
  // Built once; the decision logic lives in packages/ui.
  const notificationService = useMemo(
    () => createWebNotificationService(openConversationFromNotification),
    [openConversationFromNotification],
  );
  useEffect(() => initWebNotifications(), []);

  // Suppress in-app notifications for a conversation when the IncomingCallBanner
  // is already ringing for it — the banner is the intended call UX, a chat
  // notification on top would be redundant and confusing.
  const notificationHandle = useCallback<typeof notificationService.handle>(
    (evt) => {
      if (evt.conversationId !== undefined) {
        // Per-call MLS groups must never surface as "new conversation" banners.
        // `evt.conversationId` from persistIfNew is the CALL GROUP id; ring.conversationId
        // is the HOST conversation id — they are DIFFERENT, so the ring loop below
        // can never match. The registry check is the only reliable guard here.
        if (evt.kind === 'invite' && isRegisteredCallGroup(evt.conversationId)) return;
        const rings = useCallStore.getState().rings;
        for (const [, ring] of rings) {
          if (ring.conversationId === evt.conversationId && ring.status === 'ringing') return;
        }
      }
      notificationService.handle(evt);
    },
    [notificationService],
  );

  useAccountEventNotifications({
    transport: getCurrentTransport(),
    onNotify: notificationHandle,
  });

  // Subscribe to SDK `onConversationUpdated` events so peer-initiated
  // chats (delivered via the Welcome fan-out path) land in the local
  // Dexie `conversations` table and surface in ConversationListPanel
  // without a manual refresh.
  useIncomingConversations(notificationHandle);
  // Subscribe to SDK `onMessage` and write decrypted application
  // messages to Dexie's `messages` table so incoming bubbles render
  // in the chat thread. Bypasses packages/ui's syncEngine path
  // because its start/stop dance races StrictMode and its parse
  // step was written against a pre-SDK envelope shape.
  // The shared hook now also recognises `com.ping.voice` envelopes
  // and persists the full envelope JSON as the row's `body`, so the
  // chat surface's per-message variant dispatcher
  // (`parseVoiceEnvelope`) can decode it without a sidecar table.
  // The previous parallel writer (`useIncomingMedia`) is gone —
  // single source of truth eliminates the dedupe race on contentHash.
  useIncomingMessages(notificationHandle);

  // Fire device-local meeting reminders (§2.13) regardless of route — the
  // creator's "remind me N minutes before" choice lives in a local table and
  // fires a Notification from whichever tab is awake.
  useMeetingReminderScheduler();

  const openGlobalSearch = useCallback(() => {
    router.openModal('search');
  }, [router]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const meta = event.metaKey || event.ctrlKey;
      if (meta && event.key === ',') {
        event.preventDefault();
        router.openModal('settings');
        return;
      }
      // Cmd/Ctrl+K → open global search. Matches the universal
      // documentation-search hotkey (Linear, Algolia DocSearch, Slack,
      // GitHub). Browsers reserve Cmd+L / Cmd+T / Cmd+W / Cmd+R — Cmd+K
      // is free in every major browser.
      if (meta && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        if (router.modal.kind === 'search') {
          router.closeModal();
        } else {
          router.openModal('search');
        }
        return;
      }
      if (event.key === 'Escape' && router.modal.kind !== 'none') {
        event.preventDefault();
        router.closeModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [router]);

  // Notes + Tasks section tabs in the top bar (Figma Ping-Desktop
  // 3746:7690 / 3746:5852). They toggle the chat's right-utility panel
  // (Notes/Tasks render as a docked right panel, not a separate screen).
  // Pressing a tab opens the panel and routes to /chat so the panel is
  // visible alongside the conversation.
  const activePanel = useUiPreferencesStore((s) => s.chatUtilityPanel);
  const toggleChatUtilityPanel = useUiPreferencesStore((s) => s.toggleChatUtilityPanel);
  const openPanel = useCallback(
    (panel: 'notes' | 'tasks') => {
      if (!router.route.startsWith('/chat')) router.push('/chat');
      toggleChatUtilityPanel(panel);
    },
    [router, toggleChatUtilityPanel],
  );
  const topBarTabs = [
    {
      key: 'notes',
      label: 'Notes',
      icon: NotesTabIcon,
      active: activePanel === 'notes',
      onPress: () => openPanel('notes'),
    },
    {
      key: 'tasks',
      label: 'Tasks',
      icon: TasksTabIcon,
      active: activePanel === 'tasks',
      onPress: () => openPanel('tasks'),
    },
  ];

  return (
    <RecoveryGuideProvider>
      <AppShellUi
        topBar={<TopBar onOpenSearch={openGlobalSearch} tabs={topBarTabs} />}
        sidebar={<Sidebar />}
        main={<MainContent />}
        modals={
          <>
            <SettingsModalHost />
            <SetStatusModalHost />
            <GlobalSearchModalHost />
            <NotificationBannerHost onOpenConversation={openConversationFromNotification} />
            <ConnectivityBanner />
            <CallHost onOpenConversation={openConversationFromNotification} />
            {/* First-run product tours. Render nothing until their turn; the
                onboarding coordinator keeps them from overlapping the recovery
                nudge or each other. */}
            <HomeTourHost />
            <SettingsTourHost />
            <PaneGuideHost />
          </>
        }
      />
    </RecoveryGuideProvider>
  );
}
